import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Select, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDoctors,
  getCosmetologists,
  setOnProcess,
  finishAppointment,
  getAppointmentById,
  getStockByBranchAndTreatment,
} from '../../../../appRedux/actions';
import {
  appointmentConst,
  appointmentStatus,
  appointmentStatusConst,
  buttonLabel,
  cardConfig,
  formConfig,
  label,
  messages,
  oneColumnFormConfig,
  placeHolders,
} from '../../../../constants';
import { getRedemptionBranchAuthorization } from '../../../../util';
import { CustomSelect } from '../../../uiComponents';
import FormItem from 'antd/es/form/FormItem';
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { RedemptionBranchAuthorizationModal } from '../../RedemptionBranchAuthorizationModal';

export const StatusModalBody = ({ appointmentId, setIsModalOpen }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { selectedBranchId } = useSelector((state) => state.agenda);
  const { doctors, cosmetologists } = useSelector((state) => state.user);
  const { appointmentById } = useSelector((state) => state.agenda);
  const { stockForTreatment } = useSelector((state) => state.stock);
  const [response, setResponse] = useState();
  const [authorizationDetail, setAuthorizationDetail] = useState(null);
  const [pendingFinish, setPendingFinish] = useState(null);
  const [status, setStatus] = useState(false);
  const [consent, setConsent] = useState(false);
  const [needConsent, setNeedConsent] = useState(false);
  const [consentId, setConsentId] = useState();
  const [purchasedProduct, setPurchasedProduct] = useState();
  const [sessions, setSessions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roleLabel, setRoleLabel] = useState();
  const [users, setUsers] = useState([]);
  const [statusFiltered, setStatusFiltered] = useState();
  const [loadedUsersKey, setLoadedUsersKey] = useState();
  const [refreshAppointment, setRefreshAppointment] = useState(false);
  const [requiredConsent, SetRequiredConsent] = useState(false);
  const [limit, setLimit] = useState(1);
  const [areasApplied, setAreasApplied] = useState([]);
  const [appliedTreatment, setAppliedTreatment] = useState();
  const { scheduled, inProcess, finished } = appointmentStatus;
  const { treatment } = appointmentById ?? {};
  const operationalTreatment = appliedTreatment ?? treatment;
  const treatmentChanged =
    !!treatment?.id && operationalTreatment?.id !== treatment.id;
  const appliedTreatmentOptions = [
    treatment,
    ...(treatment?.relatedTreatments ?? []),
  ]
    .filter(Boolean)
    .map(({ id, name, sku }) => ({
      value: id,
      label: `${sku} - ${name}`,
    }));

  useEffect(() => {
    dispatch(getAppointmentById(appointmentId));
  }, [dispatch, appointmentId, refreshAppointment]);

  useEffect(() => {
    if (appointmentById) {
      SetRequiredConsent(!!appointmentById?.treatment?.consent);
      setLimit(appointmentById?.purchasedProduct?.limit);

      if (
        appointmentById?.treatment?.treatmentType?.name ===
          appointmentConst.DOCTOR ||
        appointmentById?.treatment?.treatmentType?.name ===
          appointmentConst.VALUATION
      ) {
        setRoleLabel(appointmentConst.DOCTOR);
      } else {
        setRoleLabel(appointmentConst.COSMETOLOGIST);
      }

      setStatusFiltered(
        appointmentStatusConst?.filter((status) => {
          switch (appointmentById?.status) {
            case scheduled?.alias:
              return status?.value !== finished?.alias;
            case inProcess?.alias:
              if (!appointmentById?.purchasedProduct) {
                return status?.value === inProcess?.alias;
              } else {
                return [finished?.alias].includes(status?.value);
              }
            default:
              return [];
          }
        })
      );
    }
  }, [appointmentById, finished?.alias, scheduled?.alias, inProcess?.alias]);

  useEffect(() => {
    const usersKey = `${roleLabel}:${selectedBranchId ?? ''}`;

    if (!roleLabel || loadedUsersKey !== usersKey) {
      setUsers([]);
      return;
    }

    setUsers(roleLabel === appointmentConst.DOCTOR ? doctors : cosmetologists);
  }, [
    roleLabel,
    selectedBranchId,
    loadedUsersKey,
    doctors,
    cosmetologists,
  ]);

  useEffect(() => {
    if (!roleLabel) return;

    const usersKey = `${roleLabel}:${selectedBranchId ?? ''}`;
    setLoadedUsersKey(undefined);

    const loadUsers =
      roleLabel === appointmentConst.DOCTOR
        ? getDoctors(selectedBranchId)
        : getCosmetologists(selectedBranchId);

    dispatch(loadUsers).then(() => setLoadedUsersKey(usersKey));
  }, [roleLabel, dispatch, selectedBranchId]);

  useEffect(() => {
    if ([200, 201].includes(response)) {
      setIsModalOpen(false);
    }
  }, [response, setIsModalOpen]);

  useEffect(() => {
    if (appointmentById?.status) {
      setStatus(appointmentById?.status);

      form.setFieldsValue({
        status: appointmentStatus[appointmentById?.status]?.value,
      });
    }
  }, [appointmentById.status, form]);

  useEffect(() => {
    if (
      appointmentById?.attendedBy &&
      Object.keys(appointmentById?.attendedBy).length > 0
    )
      form.setFieldsValue({
        attendedById: appointmentById?.attendedBy?.id,
      });
  }, [appointmentById.attendedBy, form]);

  useEffect(() => {
    if (appointmentById?.treatment) {
      setNeedConsent(appointmentById?.treatment?.consent === 1);
      setConsentId(appointmentById?.treatment?.treatmentConsent?.id);
      const selectedTreatment =
        appointmentById?.appliedTreatment ?? appointmentById.treatment;
      setAppliedTreatment(selectedTreatment);
      form.setFieldsValue({
        appliedTreatmentId: selectedTreatment.id,
        treatmentChangeReason:
          appointmentById?.treatmentChangeReason ?? undefined,
        changeRequestedByCustomer:
          appointmentById?.changeRequestedByCustomer ?? false,
      });
    }
  }, [appointmentById, form]);

  useEffect(() => {
    if (appointmentById?.consent) {
      setConsent(appointmentById?.consent);
    }
  }, [appointmentById]);

  useEffect(() => {
    if (appointmentById?.purchasedProduct)
      setPurchasedProduct(appointmentById?.purchasedProduct);
  }, [appointmentById?.purchasedProduct]);

  useEffect(() => {
    if (appointmentById?.purchasedProduct) {
      const { sessions, currentSession } = appointmentById?.purchasedProduct;
      const { sessions: sessionsScheduled = 0 } = appointmentById;
      const leftSessions = sessions - currentSession + sessionsScheduled;

      const session = [];
      for (let index = 0; index < leftSessions; index++) {
        session.push({
          value: index + 1,
          label: index + 1,
        });
      }

      setSessions(session);
    }
  }, [appointmentById]);

  useEffect(() => {
    let result = [];
    if (appointmentById?.purchasedProduct?.session?.length > 0) {
      const sessions = appointmentById?.purchasedProduct?.session;
      const areasToUse = appointmentById?.purchasedProduct?.areas
        ? appointmentById?.purchasedProduct?.areas
        : appointmentById?.treatment?.areas;
      result = sessions.filter((session) =>
        areasToUse.map((area) => area.id).includes(session?.area?.id)
      );

      const selected = result.map((value) => value.area);

      setAreasApplied(() => {
        return removeDuplicates(selected);
      });
    }
  }, [appointmentById, limit]);

  useEffect(() => {
    const areasToUse = appointmentById?.purchasedProduct?.areas
      ? appointmentById?.purchasedProduct?.areas
      : appointmentById?.treatment?.areas;
    if (areasApplied.length >= limit) {
      setAreas(areasToUse.filter((area) => areasApplied.includes(area.id)));
    } else {
      setAreas(areasToUse);
    }
  }, [areasApplied, limit, appointmentById]);

  useEffect(() => {
    if (status === finished?.alias && operationalTreatment?.id) {
      dispatch(
        getStockByBranchAndTreatment(
          selectedBranchId,
          operationalTreatment.id
        )
      );
    }
  }, [
    status,
    finished,
    dispatch,
    selectedBranchId,
    operationalTreatment,
  ]);

  const onAreaChange = () => {
    const sessionAreas = form
      .getFieldsValue()
      .sessionAreas.map((sA) => sA.areaId)
      .filter((sA) => sA !== undefined);
    removeDuplicates(sessionAreas);
    setAreasApplied(sessionAreas);
  };

  const removeDuplicates = (selectedAreas) => {
    return selectedAreas.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t) => t.place === value.place && t.name === value.name)
    );
  };

  const downloadConsent = () => {
    window.open(
      `${process.env.REACT_APP_API}/v${process.env.REACT_APP_API_VERSION}/consent/${consentId}/${appointmentById?.id}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const onSessionsChange = (sessions) => {
    const treatmentProducts = [];
    for (const treatmentProduct of
      operationalTreatment?.treatmentProduct ?? []) {
      const requiredQuantity = treatmentProduct.quantity * sessions;
      for (let i = 0; i < requiredQuantity; i++) {
        treatmentProducts.push({
          productId: treatmentProduct.product.id,
        });
      }
    }

    const sessionAreas = [];
    for (let i = 0; i < sessions; i++) {
      sessionAreas.push('');
    }

    form.setFieldsValue({ sessionAreas, treatmentProducts });
  };

  const onAppliedTreatmentChange = (treatmentId) => {
    const selectedTreatment = [
      treatment,
      ...(treatment?.relatedTreatments ?? []),
    ].find((item) => item?.id === treatmentId);

    setAppliedTreatment(selectedTreatment);
    form.setFieldsValue({
      sessions: undefined,
      sessionAreas: [],
      treatmentProducts: [],
      treatmentChangeReason: undefined,
      changeRequestedByCustomer: false,
    });
  };

  const getTreatmentProduct = (fieldName) => {
    const productId = form.getFieldValue([
      'treatmentProducts',
      fieldName,
      'productId',
    ]);

    return operationalTreatment?.treatmentProduct?.find(
      (treatmentProduct) => treatmentProduct.product.id === productId
    );
  };

  const onSubmit = async (formValues) => {
    const { treatmentProducts, ...payload } = formValues;
    payload.status = status;

    if (status === finished?.alias && treatmentProducts) {
      payload.stockIds = treatmentProducts.map(
        (product) => product.stockId.value
      );
    }

    if (status === finished?.alias) {
      const result = await dispatch(
        finishAppointment(appointmentById?.id, payload)
      );
      const authorization = getRedemptionBranchAuthorization(result);
      if (authorization) {
        setAuthorizationDetail(authorization);
        setPendingFinish(payload);
      } else {
        setResponse(result);
      }
    } else {
      delete payload.appliedTreatmentId;
      delete payload.treatmentChangeReason;
      delete payload.changeRequestedByCustomer;

      setResponse(
        await dispatch(setOnProcess(appointmentById?.id, payload))
      );
    }
  };

  return (
    <Card size="small" bordered={true} style={cardConfig}>
      {!purchasedProduct && (
        <Alert
          message={appointmentConst.PURCHASED_PRODUCT_ALERT.TITLE}
          description={appointmentConst.PURCHASED_PRODUCT_ALERT.DESCRIPTION}
          showIcon
          type="warning"
          action={
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => setRefreshAppointment(!refreshAppointment)}
            >
              {buttonLabel.refresh}
            </Button>
          }
        />
      )}

      <Form autoComplete="off" onFinish={onSubmit} form={form} {...formConfig}>
        {needConsent && (
          <Form.Item name="consent" label={label.consent}>
            <Switch
              checked={consent}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              onChange={setConsent}
              disabled={appointmentById?.consent}
            />
          </Form.Item>
        )}

        {(consent || !needConsent) && (
          <FormItem name="status" label={label.status}>
            <Select
              placeholder={placeHolders.status}
              onChange={setStatus}
              options={statusFiltered}
            ></Select>
          </FormItem>
        )}

        {[inProcess?.alias, finished?.alias].includes(status) && (
          <Form.Item name="attendedById" label={`${roleLabel}:`}>
            <CustomSelect
              placeholder={placeHolders.assign}
              options={users?.map((user) => ({
                value: user?.id,
                label: `${user?.name} ${user?.lastName}`,
              }))}
            ></CustomSelect>
          </Form.Item>
        )}

        {status === finished?.alias && (
          <>
            <Form.Item
              {...formConfig}
              name="appliedTreatmentId"
              label={label.appliedTreatment}
              rules={[
                {
                  required: true,
                  message: messages.required(label.appliedTreatment),
                },
              ]}
            >
              <CustomSelect
                placeholder={placeHolders.appliedTreatment}
                onChange={onAppliedTreatmentChange}
                options={appliedTreatmentOptions}
              />
            </Form.Item>

            {treatmentChanged && (
              <>
                <Form.Item
                  {...formConfig}
                  name="treatmentChangeReason"
                  label={label.treatmentChangeReason}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: messages.required(label.treatmentChangeReason),
                    },
                  ]}
                >
                  <Input.TextArea
                    maxLength={1000}
                    placeholder={placeHolders.treatmentChangeReason}
                  />
                </Form.Item>

                <Form.Item
                  {...formConfig}
                  name="changeRequestedByCustomer"
                  label={label.changeRequestedByCustomer}
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              {...formConfig}
              name="sessions"
              label={label.sessions}
              rules={[
                {
                  required: true,
                  message: messages.required(label.sessions),
                },
              ]}
            >
              <Select
                allowClear
                onChange={onSessionsChange}
                options={sessions}
              ></Select>
            </Form.Item>

            {areas.length > 0 && (
              <Form.List {...formConfig} disabled name="sessionAreas">
                {(fields) => (
                  <div>
                    {fields.map((field) => (
                      <Form.Item
                        {...formConfig}
                        name={[field.name, 'areaId']}
                        label={label.sessionAreas(field.name + 1)}
                        rules={[
                          {
                            required: true,
                            message: messages.required(label.areas),
                          },
                        ]}
                      >
                        <CustomSelect
                          placeholder={placeHolders.aplication}
                          allowClear
                          onChange={onAreaChange}
                          options={areas?.map(({ id, name }) => ({
                            value: id,
                            label: name,
                          }))}
                        ></CustomSelect>
                      </Form.Item>
                    ))}
                  </div>
                )}
              </Form.List>
            )}

            <Form.List name="treatmentProducts">
              {(fields) => (
                <div className="products-container">
                  {fields.map((field) => {
                    const treatmentProduct = getTreatmentProduct(field.name);
                    const availableStock = (stockForTreatment ?? []).filter(
                      (product) =>
                        product.productId === treatmentProduct?.product?.id
                    );

                    return (
                      <div key={field.key}>
                        <Form.Item
                          name={[field.name, 'productId']}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                      <Form.Item
                        {...oneColumnFormConfig}
                        label={`${label.product}: ${
                          treatmentProduct?.product?.name ?? ''
                        }`}
                        name={[field.name, 'stockId']}
                        rules={[
                          {
                            required: true,
                            message: messages.required(label.product),
                          },
                        ]}
                      >
                        <CustomSelect
                          placeholder={placeHolders.product}
                          labelInValue={true}
                          options={availableStock.map((product) => ({
                            value: product?.stockId,
                            label: `SKU: ${product?.sku} - Lote: ${product?.allotment} - ${product?.name}`,
                            productId: product?.productId,
                            stock: product?.stock,
                          }))}
                        ></CustomSelect>
                      </Form.Item>
                      </div>
                    );
                  })}
                </div>
              )}
            </Form.List>
          </>
        )}

        {requiredConsent && (
          <Button
            type="default"
            onClick={() => downloadConsent()}
            icon={<DownloadOutlined />}
          >
            {buttonLabel.consent}
          </Button>
        )}

        <Button type="primary" htmlType="submit" className="btn-form">
          {buttonLabel.save}
        </Button>
      </Form>
      <RedemptionBranchAuthorizationModal
        open={!!authorizationDetail}
        detail={authorizationDetail}
        onCancel={() => {
          setAuthorizationDetail(null);
          setPendingFinish(null);
        }}
        onConfirm={async (authorization) => {
          const result = await dispatch(
            finishAppointment(appointmentById?.id, {
              ...pendingFinish,
              ...authorization,
            })
          );
          if ([200, 201].includes(result)) {
            setAuthorizationDetail(null);
            setPendingFinish(null);
            setResponse(result);
            return true;
          }
          return false;
        }}
      />
    </Card>
  );
};
